import type { DbClient } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { buildMatchScoreQuery } from "./score-listings-for-match.ts";
import {
    UrlSearchParamsSchema,
    type UrlSearchParams,
} from "@/features/discovery";

const loaderDeps = ({ search }: { search: UrlSearchParams }) => ({
    ...search,
    lat: search.lat as number | undefined,
    lng: search.lng as number | undefined,
    page: search.page as number | undefined,
});

export const matchRoute = {
    validateSearch: UrlSearchParamsSchema,
    loaderDeps,
    ssr: false,
    beforeLoad: async ({ search }: { search: UrlSearchParams }) => {
        if (search.lat == null || search.lng == null) {
            return;
        }
        const limit = Math.min(50, Math.max(1, search.limit ?? 50));
        if (search.page !== 0 || search.limit !== limit) {
            throw redirect({
                to: "/match",
                search: { ...search, page: 0, limit },
            });
        }
    },
    loader: async ({
        context,
        deps,
    }: {
        context: { queryClient: QueryClient; dbClient: DbClient };
        deps: ReturnType<typeof loaderDeps>;
    }) => {
        if (deps.lat == null || deps.lng == null) {
            return { cleanedSearchParams: null };
        }

        void import("@/features/saved-listings").then(
            ({ getSavedListingIdsCollection }) =>
                getSavedListingIdsCollection(context.dbClient)
                    .preload()
                    .catch(() => {}),
        );
        await context.queryClient
            .ensureQueryData(
                buildMatchScoreQuery({
                    ...deps,
                    lat: deps.lat,
                    lng: deps.lng,
                    page: deps.page ?? 0,
                }),
            )
            .catch(() => {});
        return {
            cleanedSearchParams: {
                ...deps,
                lat: deps.lat,
                lng: deps.lng,
                page: deps.page ?? 0,
            },
        };
    },
    head: () => ({
        meta: [
            {
                title: "Your matches — Wags",
            },
        ],
    }),
};
